<template>
    <div class="text-center">
        <v-dialog v-model="dialog" :width="width" :height="height">
            <v-card>
                <v-card-title
                    class="text-h5 bg-grey-lighten-2"
                    primary-title
                    style="display: relative"
                >
                    {{ formData.qr_code }} - {{ well_pos }}
                    <div v-if="formData.startDate" class="text-xs">
                        (D0 - {{ formData.startDate }})
                    </div>
                </v-card-title>

                <v-card-text class="pt-8" style="display: flex; flex-wrap: wrap" :height="height">
                    <div
                        class="mx-8"
                        style="width: 320px"
                        v-for="formField in formDataFields"
                        v-bind:key="formField.name"
                    >
                        <div v-if="formField.field_type === 'radio' && dayFound(formField)">
                            <div
                                v-if="
                                    formField.name !== 'leaf_health_degree' ||
                                    displayLeafHealthDegree()
                                "
                            >
                                <div class="text-body-2 font-weight-regular">
                                    {{ formField.display_name }}
                                </div>

                                <v-radio-group v-model="formData[formField.name]" inline>
                                    <span
                                        v-for="options in formField.options"
                                        v-bind:key="options.name"
                                    >
                                        <v-radio
                                            name="formField.name"
                                            :label="options.name"
                                            :modelValue="options.value"
                                        ></v-radio>
                                    </span>
                                </v-radio-group>
                            </div>
                        </div>

                        <v-row v-if="formField.field_type === 'text' && dayFound(formField)">
                            <v-col v-if="formField.name === 'total_dead_count'">
                                <v-text-field
                                    v-bind:style="bcc[formField.name]"
                                    type="text"
                                    pattern="[0-9]*"
                                    decimal
                                    v-model="formData[formField.name]"
                                    :label="formField.display_name || formField.name"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col class="pb-5" v-if="formField.name != 'total_dead_count'">
                                <v-text-field
                                    :ref="formField.name"
                                    v-bind:style="bcc[formField.name]"
                                    type="text"
                                    pattern="[0-9]*"
                                    decimal
                                    v-model="formData[formField.name]"
                                    :label="formField.display_name || formField.name"
                                >
                                    <template v-slot:append>
                                        {{ formField.appendText }}
                                    </template>
                                </v-text-field>

                                <div v-if="formField.name === 'incremental_dead_count'">
                                    (Total Dead - {{ totalDead }})
                                </div>
                            </v-col>
                            <v-col
                                class="pl-5"
                                v-if="
                                    formField.name === 'incremental_dead_count' &&
                                    formData.data_array &&
                                    formData.data_array.length
                                "
                            >
                                <table style="color: rgba(0, 0, 0, 0.54); font-size: 12px">
                                    <tbody>
                                        <tr>
                                            <td>Date</td>
                                            <td style="padding-left: 10px">Daily Dead</td>
                                            <td style="padding-left: 10px">Total Alive</td>
                                        </tr>
                                        <tr
                                            v-for="(dayData, index) in formData.data_array"
                                            :key="index"
                                        >
                                            <td>{{ formatDate(dayData.date_measured) }}</td>
                                            <td style="padding-left: 10px; text-align: center">
                                                {{ dayData.incremental_dead_count }}
                                            </td>
                                            <td style="padding-left: 10px; text-align: center">
                                                {{ totalAliveOnDay(dayData.date_measured) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </v-col>
                            <v-col v-if="formField.name === 'total_dead_count'">
                                (Incremental Dead - {{ incrementalDead }})
                            </v-col>
                        </v-row>

                        <v-row v-if="formField.field_type === 'textarea' && dayFound(formField)">
                            <v-col>
                                <v-textarea
                                    :density="true ? 'compact' : undefined"
                                    :label="formField.display_name || formField.name"
                                    v-model="formData[formField.name]"
                                    rows="1"
                                ></v-textarea>
                            </v-col>
                        </v-row>

                        <v-row v-if="formField.field_type === 'slider' && dayFound(formField)">
                            <v-col>
                                <div class="text-subtitle-2 pl-0">{{ formField.display_name }}</div>
                                <v-slider
                                    v-model="formData[formField.name]"
                                    :min="formField.min"
                                    :max="formField.max"
                                    thumb-label="always"
                                    required
                                    density="compact"
                                ></v-slider>
                            </v-col>
                        </v-row>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="cancelIt"> Cancel </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { defineComponent } from "vue";

import { EventBus } from "../../lib/event-bus.js";
import axios from "../../lib/apiAxios";
import moment from "moment";
import _ from "lodash";

export default defineComponent({
    name: "well-form",

    props: {
        datemeasured: String,
    },

    created: function () {
        EventBus.$on("dialog-sub", this.dialogSub);
    },

    methods: {
        focusInput: function (fieldRefName) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const self = this;
            setTimeout(() => {
                if (_.has(self.$refs, fieldRefName)) {
                    if (_.isArray(self.$refs[fieldRefName])) {
                        self.$refs[fieldRefName][0].$refs.input.focus();
                    } else {
                        self.$refs[fieldRefName].$refs.input.focus();
                    }
                }
            }, 1);
        },

        dialogSub: function (data) {
            this.dialog = 1;
            this.well_pos = data.wellData.well_pos;

            let fields = data.wellData.data;
            let formData = {};
            let keys = Object.keys(fields);

            keys.forEach((property) => {
                formData[property] = fields[property];
            });
            formData["date_measured"] = data.wellData.date_measured;
            formData["qr_code"] = data.wellData.qr_code;
            formData["id"] = data.wellData.id;
            this.formData = formData;
            this.formDataFields = data.outputs;
            this.width = 1200;
            this.height = data.height || 500;
        },
        totalAliveOnDay: function (theDate) {
            if (!this.formData.data_array.length || !this.formData.data_array[0].live_count) {
                return "N/A";
            }
            let totalAlive = this.formData.data_array[0].live_count;
            this.formData.data_array.forEach((day) => {
                if (
                    day.incremental_dead_count &&
                    moment(day.date_measured).isSameOrBefore(theDate)
                ) {
                    totalAlive -= parseInt(day.incremental_dead_count);
                }
            });
            return totalAlive;
        },

        formatDate: function (dt) {
            let tmpDate = moment(dt).format("M/D");
            return tmpDate;
        },

        close: function () {
            this.$emit("updatewell", null);
        },

        cancelIt: function () {
            this.dialog = false;
            this.$router.push({
                path: `/experiment/${this.$route.params.code}/${this.$route.params.protocolId}/${this.$route.params.date}`,
            });
        },

        save: function () {
            let cloneFormData = this._.cloneDeep(this.formData);

            // loop through all attributes of data
            this._.forOwn(this.formData, (value, key) => {
                if (key !== "data" && isNaN(key)) {
                    this.formDataFields.outputRules.forEach((dataElement) => {
                        if (dataElement.name === key) {
                            dataElement.value = value;
                            delete cloneFormData[key];

                            let found = false;
                            cloneFormData.forEach((ele) => {
                                if (ele.name === key) {
                                    ele.value = value;
                                    found = true;
                                }
                            });

                            if (!found) cloneFormData.push({ name: key, value: value });
                        }
                    });
                }
            });

            this.$emit("updatewell", { data: cloneFormData, well: this.well_pos });
            EventBus.$emit("form-saved", this.$route.params.QRcode);
            console.log("well has been updated");
            this.$refs.modal.close();
        },

        dayFound: function (field) {
            if (!field.day) {
                return true;
            }
            if (field.day.indexOf(this.dayDiff) !== -1) {
                return true;
            }

            return false;
        },
        displayLeafHealthDegree: function () {
            let returnStatus = false;
            this.formDataFields.forEach((formField) => {
                if (formField.name === "leaf_health") {
                    if (this.formData.leaf_health !== true) {
                        returnStatus = true;
                    }
                }
            });
            return returnStatus;
        },

        refresh: function () {
            this.$forceUpdate();
        },
    },

    computed: {
        totalDead: function () {
            console.log("computing");
            let count = 0;

            if (this.formData.data_array) {
                this.formData.data_array.forEach((val) => {
                    console.log(val.incremental_dead_count);
                    if (val.incremental_dead_count) {
                        count += parseInt(val.incremental_dead_count);
                    }
                });
            }
            return count;
        },
        incrementalDead: function () {
            console.log("computing inc");
            let count = 0;
            let incrementalCount = 0;

            if (this.formData.data_array) {
                console.log("has data array");
                this.formData.data_array.forEach((val) => {
                    console.log("has incremental ", val.incremental_dead_count);
                    console.log("this date ", this.formData.date_measured);
                    console.log("array date ", val.date_measured);
                    if (
                        val.incremental_dead_count &&
                        moment(val.date_measured).isSameOrBefore(this.formData.date_measured)
                    ) {
                        if (
                            val.incremental_dead_count &&
                            val.date_measured === this.formData.date_measured
                        ) {
                            incrementalCount = parseInt(val.incremental_dead_count);
                        }
                        console.log("counting ", val.incremental_dead_count);
                        count += parseInt(val.incremental_dead_count);
                    }
                });
            }
            this.formData.total_dead_count = count;
            return incrementalCount;
        },
    },

    data: () => ({
        dialog: null,
        width: null,
        height: null,
        classicModal: false,
        sending: false,
        well_pos: null,
        formDataFields: [],
        previousText: "",
        date_measured: null,
        formData: {
            insect_mass: null,
            leaf_health: null,
            leaf_health_degree: null,
            comments: null,
        },
        bcc: {
            insect_mass: {
                backgroundColor: "",
            },
        },
        radio: true,
    }),
});
</script>

<style></style>
